import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { X, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";

export default function DeliveryCodeModal({ open, onClose, orderId, onVerified }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 4) return toast.error(t("dcm.code4"));
    setBusy(true);
    setError(null);
    try {
      const { data } = await apiClient.post(`/orders/${orderId}/verify-delivery-code`, { code });
      toast.success(t("dcm.codeOk"));
      setCode("");
      onVerified?.(data);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid="delivery-code-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-6 relative">
        <button type="button" onClick={onClose} className="absolute right-3 top-3 rounded-full p-1.5 hover:bg-elevated" aria-label={t("dcm.close")} data-testid="dc-modal-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-5 h-5 text-brand-red" />
          <h3 className="font-display text-xl font-semibold">{t("dcm.title")}</h3>
        </div>
        <p className="text-sm text-mute mb-5">{t("dcm.hintA")} <b>{t("dcm.hintB")}</b> {t("dcm.hintC")}</p>
        <input
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 4)); setError(null); }}
          inputMode="numeric"
          autoFocus
          maxLength={4}
          placeholder="0000"
          data-testid="delivery-code-input"
          className={`w-full text-center bg-white border-2 rounded-2xl px-6 py-5 text-4xl tracking-[0.5em] font-bold outline-none focus:border-brand-red ${error ? "border-brand-red" : "border-sand"}`}
        />
        {error && (
          <div className="mt-2 text-xs text-brand-red font-semibold text-center" data-testid="delivery-code-error">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy || code.length !== 4}
          data-testid="delivery-code-submit"
          className="mt-5 w-full rounded-full bg-brand-green hover:bg-[#007A4A] disabled:opacity-60 text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {t("dcm.submit")}
        </button>
      </form>
    </div>
  );
}
